/**
 * VA Claim Types catalog with required forms per type.
 *
 * Each claim type lists the forms most veterans need to submit (or strongly
 * benefit from including) for that path. Optional supporting forms are flagged
 * with `optional: true`. Form numbers map to entries in `vaFormsCatalog.js` —
 * keep them in sync.
 *
 * Sources:
 *  - 38 CFR § 3.155, § 3.2501, § 19.2(d), § 20.202
 *  - VA.gov: How to file a claim, How to appeal a decision
 *  - M21-1, Part III, Subpart i
 */

export const CLAIM_TYPES = [
  {
    id: "original",
    label: "Original disability compensation",
    blurb: "Your first VA disability claim for one or more service-connected conditions.",
    forms: [
      { num: "21-526EZ", role: "Primary application", optional: false },
      { num: "21-4142", role: "Authorization to release private medical records", optional: false },
      { num: "21-4142a", role: "List of medical providers (companion to 21-4142)", optional: false },
      { num: "21-0966", role: "Intent to File (lock in your effective date while gathering evidence)", optional: true },
      { num: "21-10210", role: "Lay/buddy statement(s)", optional: true },
      { num: "SF-180", role: "Request DD-214 / service records if VA does not already have them", optional: true },
    ],
  },
  {
    id: "increase",
    label: "Increased evaluation (existing condition got worse)",
    blurb: "Your current rated condition has worsened and you want a higher rating.",
    forms: [
      { num: "21-526EZ", role: "Primary application (claim for increase)", optional: false },
      { num: "21-4142", role: "Authorization for updated treatment records", optional: false },
      { num: "21-4142a", role: "Provider list for new treatment", optional: false },
      { num: "21-10210", role: "Lay statement describing the worsening", optional: true },
    ],
  },
  {
    id: "secondary",
    label: "Secondary service connection",
    blurb: "A new condition caused or aggravated by an existing service-connected condition.",
    forms: [
      { num: "21-526EZ", role: "Primary application (claim the new secondary condition)", optional: false },
      { num: "21-4142", role: "Authorization for private medical records / nexus opinion", optional: false },
      { num: "21-4142a", role: "List of providers for the secondary condition", optional: false },
      { num: "21-10210", role: "Lay statement supporting the cause-and-effect link", optional: true },
    ],
  },
  {
    id: "new",
    label: "New condition (in addition to existing service-connected)",
    blurb: "Filing for a brand-new direct service-connection while you already have other rated conditions.",
    forms: [
      { num: "21-526EZ", role: "Primary application", optional: false },
      { num: "21-4142", role: "Authorization to release private records", optional: false },
      { num: "21-4142a", role: "Provider list", optional: false },
      { num: "21-10210", role: "Lay statement", optional: true },
    ],
  },
  {
    id: "supplemental",
    label: "Supplemental Claim (new & relevant evidence)",
    blurb: "Reopen a previously denied claim with new and relevant evidence. 38 CFR § 3.2501.",
    forms: [
      { num: "20-0995", role: "Decision Review Request: Supplemental Claim", optional: false },
      { num: "21-4142", role: "Authorization for any new private records", optional: true },
      { num: "21-4142a", role: "Provider list for any new evidence", optional: true },
      { num: "21-10210", role: "Lay statement (qualifies as new & relevant)", optional: true },
    ],
  },
  {
    id: "hlr",
    label: "Higher-Level Review",
    blurb: "Senior reviewer takes a fresh look at the same evidence. No new evidence allowed.",
    forms: [
      { num: "20-0996", role: "Decision Review Request: Higher-Level Review", optional: false },
    ],
  },
  {
    id: "board-appeal",
    label: "Board Appeal (Notice of Disagreement)",
    blurb: "Appeal a decision to the Board of Veterans' Appeals. Pick a lane: Direct review, Evidence submission, or Hearing.",
    forms: [
      { num: "10182", role: "Decision Review Request: Board Appeal (Notice of Disagreement)", optional: false },
      { num: "21-686c", role: "Update dependents if status changed during the appeal", optional: true },
    ],
  },
  {
    id: "bdd",
    label: "Benefits Delivery at Discharge (BDD)",
    blurb: "File 180–90 days before separation so your rating starts the day after discharge.",
    forms: [
      { num: "21-526EZ", role: "Primary application (mark BDD on form)", optional: false },
      { num: "SF-180", role: "Service records if not yet uploaded by DoD", optional: true },
      { num: "21-4142", role: "Authorization for private medical records, if any", optional: true },
      { num: "21-4142a", role: "Provider list for private treatment", optional: true },
    ],
  },
  {
    id: "fdc",
    label: "Fully Developed Claim (FDC)",
    blurb: "You certify all evidence is submitted up front for faster processing.",
    forms: [
      { num: "21-526EZ", role: "Primary application (FDC certification on form)", optional: false },
      { num: "21-4142", role: "Authorization (only if VA still needs private records)", optional: true },
      { num: "21-4142a", role: "Provider list (only if private records still outstanding)", optional: true },
      { num: "21-10210", role: "Lay statements (submit with the FDC packet)", optional: true },
    ],
  },
  {
    id: "ptsd",
    label: "PTSD / mental-health claim",
    blurb: "PTSD or other mental-health condition. Stressor statement required when not combat-presumed.",
    forms: [
      { num: "21-526EZ", role: "Primary application", optional: false },
      { num: "21-0781", role: "Statement in Support of Claim for PTSD (non-MST stressors)", optional: false },
      { num: "21-0781a", role: "Statement for PTSD secondary to MST/personal assault", optional: true },
      { num: "21-4142", role: "Authorization for mental-health treatment records", optional: false },
      { num: "21-4142a", role: "Provider list (therapists, psychiatrists, ER visits)", optional: false },
      { num: "21-10210", role: "Buddy/lay statements corroborating the stressor", optional: true },
    ],
  },
  {
    id: "pact",
    label: "PACT Act / toxic-exposure presumptive",
    blurb: "Burn pits, Agent Orange, Camp Lejeune, radiation, and other presumptive exposures.",
    forms: [
      { num: "21-526EZ", role: "Primary application", optional: false },
      { num: "21-0966", role: "Intent to File (lock effective date back to PACT enactment if eligible)", optional: true },
      { num: "21-4142", role: "Authorization for private records of the presumptive condition", optional: false },
      { num: "21-4142a", role: "Provider list", optional: false },
      { num: "10-10EZ", role: "Enroll in VA health care for the presumptive screening", optional: true },
    ],
  },
  {
    id: "tdiu",
    label: "Total Disability based on Individual Unemployability (TDIU)",
    blurb: "Paid at the 100% rate because service-connected conditions prevent substantially gainful employment.",
    forms: [
      { num: "21-8940", role: "Veteran's Application for Increased Compensation Based on Unemployability", optional: false },
      { num: "21-4192", role: "Request for Employment Information in Connection with Claim (employer fills out)", optional: false },
      { num: "21-526EZ", role: "File concurrently if claiming a new/increased condition that triggers TDIU", optional: true },
      { num: "21-10210", role: "Lay statements from coworkers/family on functional impact", optional: true },
    ],
  },
  {
    id: "smc",
    label: "Special Monthly Compensation (SMC)",
    blurb: "Higher rate of compensation due to loss of use, aid & attendance, or housebound status.",
    forms: [
      { num: "21-2680", role: "Examination for Housebound Status or Permanent Need for A&A", optional: false },
      { num: "21-526EZ", role: "Use to claim SMC categories tied to a new/increased condition", optional: true },
    ],
  },
  {
    id: "dependents",
    label: "Add/remove dependents",
    blurb: "Add a spouse, child, or stepchild — or remove one after a divorce or death.",
    forms: [
      { num: "21-686c", role: "Declaration of Status of Dependents", optional: false },
      { num: "21-674", role: "Request for Approval of School Attendance (child 18–23 in school)", optional: true },
    ],
  },
  {
    id: "dic",
    label: "Survivor DIC (surviving spouse / child)",
    blurb: "Dependency and Indemnity Compensation for the survivor of a veteran who died from a service-connected condition.",
    forms: [
      { num: "21P-534EZ", role: "Application for DIC, Death Pension, and/or Accrued Benefits", optional: false },
      { num: "21-4142", role: "Authorization for the deceased veteran's medical records", optional: true },
      { num: "21-4142a", role: "Provider list", optional: true },
    ],
  },
  {
    id: "pension",
    label: "Non-service-connected Pension (wartime)",
    blurb: "Means-tested pension for low-income wartime veterans 65+, or permanently/totally disabled.",
    forms: [
      { num: "21P-527EZ", role: "Application for Veterans Pension", optional: false },
      { num: "21-2680", role: "Add Aid & Attendance / Housebound to the pension", optional: true },
    ],
  },
];

export const CLAIM_TYPE_LABELS = CLAIM_TYPES.map((c) => c.label);

export function findClaimType(idOrLabel) {
  if (!idOrLabel) return null;
  const needle = String(idOrLabel).toLowerCase().trim();
  return (
    CLAIM_TYPES.find((c) => c.id === needle) ||
    CLAIM_TYPES.find((c) => c.label.toLowerCase() === needle) ||
    null
  );
}
